export type AppVersionInfo = {
  version: string
  build: string
  builtAt: string
}

export type BuildInfoFile = {
  version?: string
  build?: string
  builtAt?: string
}

let cachedLocalFallbacks: { build: string; builtAt: string } | null = null

function formatLocalBuildStamp(date: Date): string {
  const iso = date.toISOString()
  const day = iso.slice(0, 10).replaceAll('-', '')
  const time = iso.slice(11, 19).replaceAll(':', '')
  return `${day}-${time}`
}

function trimNonEmpty(value: unknown): string | null {
  if (value === undefined || value === null) {
    return null
  }
  const trimmed = String(value).trim()
  return trimmed.length > 0 ? trimmed : null
}

function getLocalFallbacks(prefix: 'local' | 'local-compose' = 'local'): {
  build: string
  builtAt: string
} {
  if (cachedLocalFallbacks) {
    return cachedLocalFallbacks
  }
  const now = new Date()
  cachedLocalFallbacks = {
    build: `${prefix}-${formatLocalBuildStamp(now)}`,
    builtAt: now.toISOString(),
  }
  return cachedLocalFallbacks
}

export type ResolveAppVersionInput = {
  envVersion?: unknown
  envBuild?: unknown
  envBuiltAt?: unknown
  packageVersion?: string
  localBuildPrefix?: 'local' | 'local-compose'
  buildInfo?: BuildInfoFile | null
}

/**
 * Priorité : env → build-info → package version + fallback local figé.
 */
export function resolveAppVersionInfo(input: ResolveAppVersionInput = {}): AppVersionInfo {
  const local = getLocalFallbacks(input.localBuildPrefix ?? 'local')
  const buildInfo = input.buildInfo ?? null

  const version =
    trimNonEmpty(input.envVersion) ??
    trimNonEmpty(buildInfo?.version) ??
    trimNonEmpty(input.packageVersion) ??
    '0.0.0'

  const build =
    trimNonEmpty(input.envBuild) ?? trimNonEmpty(buildInfo?.build) ?? local.build

  const builtAt =
    trimNonEmpty(input.envBuiltAt) ?? trimNonEmpty(buildInfo?.builtAt) ?? local.builtAt

  return { version, build, builtAt }
}

export function formatBuiltAtForDisplay(builtAt: string): string {
  const parsed = new Date(builtAt)
  if (Number.isNaN(parsed.getTime())) {
    return builtAt
  }
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed)
}
